import {
  type HazardCategory,
  HazardType,
  HealthHazard,
  PhysicalHazard,
} from "encamp-prisma/generated/browser";

/** A pre-2027 EPCRA hazard value: the two legacy enums share no members. */
export type LegacyHazard = PhysicalHazard | HealthHazard;

export type LegacyRollupCategory = Pick<
  HazardCategory,
  "code" | "selectable" | "legacyPhysicalHazard" | "legacyHealthHazard"
>;

/** The legacy category a 2027 row rolls up to, or null for the orphans. */
export function legacyHazardOf(
  category: Pick<HazardCategory, "legacyPhysicalHazard" | "legacyHealthHazard">
): LegacyHazard | null {
  return category.legacyPhysicalHazard ?? category.legacyHealthHazard ?? null;
}

/**
 * Legacy value -> the one selectable 2027 code it maps to, for legacy values
 * with exactly one selectable row. Everything else fans out and needs an SDS.
 * Derived from the rows rather than hardcoded so it cannot drift from the seed.
 */
export function buildOneToOneLegacyMap(
  categories: readonly LegacyRollupCategory[]
): Map<LegacyHazard, string> {
  const codesByLegacy = new Map<LegacyHazard, string[]>();
  for (const category of categories) {
    const legacy = legacyHazardOf(category);
    if (!category.selectable || legacy === null) {
      continue;
    }
    codesByLegacy.set(legacy, [...(codesByLegacy.get(legacy) ?? []), category.code]);
  }

  const oneToOne = new Map<LegacyHazard, string>();
  for (const [legacy, codes] of codesByLegacy) {
    if (codes.length === 1) {
      oneToOne.set(legacy, codes[0]);
    }
  }
  return oneToOne;
}

export type LegacyDerivationCategory = Pick<
  HazardCategory,
  "hazardType" | "legacyPhysicalHazard" | "legacyHealthHazard"
>;

export type DerivedLegacyHazards = {
  physicalHazards: PhysicalHazard[];
  healthHazards: HealthHazard[];
};

/**
 * The legacy arrays a chemical edited in hazard category terms should carry: each
 * category's crosswalk rollup, or HNOC of its type when EPA lists none.
 * Ordered by enum declaration so equal selections compare equal.
 */
export function legacyHazardsFromCategories(
  categories: readonly LegacyDerivationCategory[]
): DerivedLegacyHazards {
  const physical = new Set<PhysicalHazard>();
  const health = new Set<HealthHazard>();
  for (const category of categories) {
    if (category.hazardType === HazardType.PHYSICAL) {
      physical.add(category.legacyPhysicalHazard ?? PhysicalHazard.UNCLASSIFIED_PHYSICAL_HAZARD);
    } else {
      health.add(
        category.legacyHealthHazard ?? HealthHazard.HAZARD_NOT_OTHERWISE_CLASSIFIED_HEALTH
      );
    }
  }
  return {
    physicalHazards: Object.values(PhysicalHazard).filter((hazard) => physical.has(hazard)),
    healthHazards: Object.values(HealthHazard).filter((hazard) => health.has(hazard)),
  };
}
